//# sourceMappingURL=result_.mjs.map
/// <reference types="./result_.d.mts" />
import * as $result from "../../gleam_stdlib/gleam/result.mjs";
import { Ok, Error } from "../gleam.mjs";
import * as $function from "../tiamat/function.mjs";

/**
 * Takes a result and a single function, calls that function with the value
 * of the result if it's `Ok`, and returns the result instead of the function
 * return value. If it's an error, the function will not run.
 *
 * Useful for running synchronous side effects in a pipeline, while acting
 * only when results are `Ok`.
 *
 * ```gleam
 * result_.tap(Ok(1), fn (x) { int.to_string(x) })    // Ok(1)
 * result_.tap(Error(Nil), fn (x) { x + 1 })          // Error(Nil)
 * ```
 */
export function tap(res, next) {
  if (res instanceof Ok) {
    let value = res[0];
    next(value);
    return res;
  } else {
    return res;
  }
}

/**
 * Takes a result and a single function, calls that function with the value
 * of the result if it's `Error`, and returns the result instead of the function
 * return value. If it's an ok, the function will not run.
 *
 * Useful for running synchronous side effects in a pipeline, while acting
 * only when results are `Error`.
 *
 * ```gleam
 * result_.tap_error(Ok(1), fn (x) { int.to_string(x) })            // Ok(1)
 * result_.tap_error(Error(Nil), fn (x: Nil) { io.println("Nil") }) // Error(Nil)
 * ```
 */
export function tap_error(res, next) {
  if (res instanceof Ok) {
    return res;
  } else {
    let error = res[0];
    next(error);
    return new Error(error);
  }
}

/**
 * Takes a result and a single function, calls that function with the value
 * of the result if it's `Ok`, and returns the result instead of the function
 * return value if the return value itself is `Ok`. If the return value is an
 * error, return that error.
 * If the result is an error, the function will not run.
 *
 * Useful for running synchronous side effects in a pipeline, while acting
 * only when results are `Ok`.
 *
 * ```gleam
 * result_.try_tap(Ok(1), fn (x) { Ok(int.to_string(x)) })       // Ok(1)
 * result_.try_tap(Ok(1), fn (x) { Error(Nil) })                 // Error(Nil)
 * result_.try_tap(Error(Nil), fn (x) { Ok(x + 1) })             // Error(Nil)
 * result_.try_tap(Error("first"), fn (x) { Error("second") })   // Error("second")
 * ```
 */
export function try_tap(res, next) {
  if (res instanceof Ok) {
    let value = res[0];
    let $ = next(value);
    if ($ instanceof Ok) {
      return new Ok(value);
    } else {
      return $;
    }
  } else {
    return res;
  }
}

/**
 * Takes a result, a fallback value and a single function. If the value of the
 * result is `Ok`, calls the function with the value of the result and returns
 * the function's result. Otherwise the result is an error, so it returns the
 * fallback value without running the function.
 *
 * Useful to replace the pattern `result.map(...) |> result.unwrap(...)` while
 * keeping the next function at top-level.
 *
 * ```gleam
 * result_.map_unwrap(Ok(1), "NaN", fn(x) { int.to_string(x) })       // "1"
 * result_.map_unwrap(Error(Nil), "NaN", fn(x) { int.to_string(x) }   // "NaN"
 * use x <- result_.map_unwrap(Ok(1), "NaN")
 * int.to_string(x)                                                   // "1"
 * ```
 */
export function map_unwrap(res, fallback, next) {
  let _pipe = res;
  let _pipe$1 = $result.map(_pipe, next);
  return $result.unwrap(_pipe$1, fallback);
}

export function try_unwrap(res, fallback, next) {
  let _pipe = res;
  let _pipe$1 = $result.try$(_pipe, next);
  return $result.unwrap(_pipe$1, fallback);
}

export function try_lazy_unwrap(res, fallback, next) {
  let _pipe = res;
  let _pipe$1 = $result.try$(_pipe, next);
  return $result.lazy_unwrap(_pipe$1, fallback);
}

export function nil_error(res) {
  return $result.map_error(res, $function.constant(undefined));
}
